import { CampaignsByCategory, CampaignCategoryStats } from '@/types/api/campaigns';
import { useState } from 'react';
import BarChartEcho from './BarChartEcho';
import { RevenueDashboardWidget } from '@/utils/mixpanelUtils/events';
import { mixpanelTrack } from '@/utils/mixpanelUtils/asyncMixpanel';
import { BarChartData } from '@/types';
import { usePageMetadata } from '@/hooks';
import { getCampaignTypesBarChartHoverEventByPageType } from '@/utils/mixpanelUtils/pageTypeHelpers';
import { Avatar, Flex, Grid, Table, Typography, theme } from 'antd';
import type { ColumnType } from 'antd/es/table';
import { getBarChartColor } from './getBarChartColor';
import { formatCurrency } from '@/utils/currencyUtils';

type LegendRecord = {
  key: string;
  index: number;
  stats: CampaignCategoryStats;
};

export const CampaignTypesChartEcho = ({ campaignTypes }: { campaignTypes: CampaignsByCategory }) => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const { page: pageType } = usePageMetadata();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const isSmallScreen = !screens.sm;
  const isMediumScreen = !!screens.md;

  if (!pageType) throw new Error('Page type is required');

  const campaignTypesList = Object.entries(campaignTypes);

  const handleBarHover = (data: BarChartData) => {
    mixpanelTrack(getCampaignTypesBarChartHoverEventByPageType(pageType), {
      widget: RevenueDashboardWidget.CampaignTypes,
      page: pageType,
      campaignType: data.name,
    });
  };

  const columns: ColumnType<LegendRecord>[] = [
    {
      key: 'color',
      width: 40,
      render: (_, record) => (
        <Avatar
          size={12}
          style={{
            backgroundColor: getBarChartColor(record.index, campaignTypesList.length),
            flexShrink: 0,
          }}
        />
      ),
    },
    {
      key: 'type',
      render: (_, record) => (
        <Typography.Text strong style={{ textTransform: 'capitalize' }}>
          {record.stats.name}
        </Typography.Text>
      ),
    },
    {
      key: 'earnings',
      align: 'right',
      render: (_, record) =>
        formatCurrency(record.stats.total.amount, record.stats.total.currency),
    },
  ];

  return (
    <Flex
      vertical={isSmallScreen}
      justify="center"
      align="center"
      gap={isMediumScreen ? token.marginXL : token.marginMD}
      data-testid="campaign-types-chart"
      style={{ width: '100%', paddingInline: token.paddingLG, maxWidth: 700, marginInline: 'auto' }}
    >
      <BarChartEcho
        data={campaignTypes}
        style={{
          width: isMediumScreen ? '50%' : '100%',
          height: 250,
        }}
        selectedSegment={selectedCategory}
        setSelectedSegment={setSelectedCategory}
        ariaLabel="Campaign category distribution bar chart"
        onBarHover={handleBarHover}
      />

      <Table<LegendRecord>
        dataSource={campaignTypesList.map(([type, stats], index) => ({
          key: `campaign-${type}`,
          index,
          stats,
        }))}
        columns={columns}
        showHeader={false}
        pagination={false}
        size="small"
        style={{ flex: 1 }}
        onRow={(record) => ({
          style: {
            opacity:
              selectedCategory !== null && selectedCategory !== record.stats.name ? 0.25 : 1,
            transition: 'opacity 300ms',
          },
        })}
      />
    </Flex>
  );
};
